import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { LiquidGlassProvider } from "@/contexts/LiquidGlassContext";
import Index from "./pages/Index";
import Services from "./pages/Services";
import Book from "./pages/Book";
import Auth from "./pages/Auth";
import Account from "./pages/Account";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import CancellationPolicy from "./pages/CancellationPolicy";
import RewardsPolicy from "./pages/RewardsPolicy";
import SmsConsent from "./pages/SmsConsent";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCanceled from "./pages/PaymentCanceled";
import NotFound from "./pages/NotFound";
import Install from "./pages/Install";
import DeleteAccount from "./pages/DeleteAccount";
import AdminLayout from "./components/AdminLayout";
import ScrollToTop from "./components/ScrollToTop";
import ChatBot from "./components/ChatBot";
import RequireNameDialog from "./components/RequireNameDialog";
import RebrandBanner from "./components/RebrandBanner";
import Dashboard from "./pages/admin/Dashboard";
import AdminBookings from "./pages/admin/Bookings";
import BookingCalendar from "./pages/admin/BookingCalendar";
import Clients from "./pages/admin/Clients";
import AdminServices from "./pages/admin/Services";
import Photos from "./pages/admin/Photos";
import AdminSettings from "./pages/admin/AdminSettings";
import Rewards from "./pages/admin/Rewards";
import Revenue from "./pages/admin/Revenue";
import ClientDetail from "./pages/admin/ClientDetail";
import AdminReviews from "./pages/admin/Reviews";
import SmsConsentLog from "./pages/admin/SmsConsentLog";
import InstagramApprovals from "./pages/admin/InstagramApprovals";
import CommunicationData from "./pages/admin/CommunicationData";
import CalendarSyncHealth from "./pages/admin/CalendarSyncHealth";
import Review from "./pages/Review";
import ConfirmClient from "./pages/ConfirmClient";
import InstagramInstructions from "./pages/InstagramInstructions";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
  <LiquidGlassProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <RebrandBanner />
          <main>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/services" element={<Services />} />
            <Route path="/book" element={<Book />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/signup" element={<Auth />} />
            <Route path="/account" element={<Account />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-use" element={<TermsOfUse />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/rewards-policy" element={<RewardsPolicy />} />
            <Route path="/sms-consent" element={<SmsConsent />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-canceled" element={<PaymentCanceled />} />
            <Route path="/install" element={<Install />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/review" element={<Review />} />
            <Route path="/review/:bookingId" element={<Review />} />
            <Route path="/confirm-client" element={<ConfirmClient />} />
            <Route path="/instagram-instructions" element={<InstagramInstructions />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="calendar" element={<BookingCalendar />} />
              <Route path="bookings" element={<AdminBookings />} />
              <Route path="clients" element={<Clients />} />
              <Route path="clients/:userId" element={<ClientDetail />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="photos" element={<Photos />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="rewards" element={<Rewards />} />
              <Route path="revenue" element={<Revenue />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="sms-consent" element={<SmsConsentLog />} />
              <Route path="instagram-approvals" element={<InstagramApprovals />} />
              <Route path="calendar-sync-health" element={<CalendarSyncHealth />} />
              <Route path="communication-data" element={<CommunicationData />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </main>
          <ChatBot />
          <RequireNameDialog />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </LiquidGlassProvider>
  </ThemeProvider>
  </HelmetProvider>
);

export default App;
